import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
  Output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { sectionTitleKey } from '../data/section-title-keys';
import { SectionSaveStatus } from '../../../app/components/credit/shared/services/cradit.service';

export interface DocumentTab {
  id: string;
  title: string;
}

@Component({
  selector: 'app-document-tabs',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './document-tabs.html',
  styleUrl: './document-tabs.css',
})
export class DocumentTabs implements OnChanges, AfterViewInit {
  @Input() tabs: DocumentTab[] = [];
  @Input() activeIndex = 0;
  @Input() statuses: Record<string, SectionSaveStatus> = {};
  @Input() panelId = 'document-section-content';

  @Output() activeIndexChange = new EventEmitter<number>();

  readonly sectionTitleKey = sectionTitleKey;

  /** Explains the tab status icons; rendered with the same icon template as the tabs. */
  readonly legendItems: ReadonlyArray<{ status: SectionSaveStatus; label: string }> = [
    { status: 'idle', label: 'DOC_BUILDER.TABS.LEGEND_NOT_SAVED' },
    { status: 'dirty', label: 'DOC_BUILDER.TABS.LEGEND_UNSAVED' },
    { status: 'saved', label: 'DOC_BUILDER.TABS.LEGEND_SAVED' },
    { status: 'api-error', label: 'DOC_BUILDER.TABS.LEGEND_ERROR' },
  ];

  @ViewChildren('tabButton') private tabButtons!: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChildren('scrollTrack', { read: ElementRef })
  private scrollTrackRef?: QueryList<ElementRef<HTMLDivElement>>;

  canScrollLeft = false;
  canScrollRight = false;
  hasOverflow = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeIndex']) {
      // Wait a tick so the DOM reflects the new aria-selected/tabindex state.
      setTimeout(() => this.scrollActiveTabIntoView());
    }
  }

  ngAfterViewInit(): void {
    // Deferred: `scrollActiveTabIntoView()` (below) already calls
    // `updateScrollAffordance()`. Calling it synchronously here too would
    // mutate `canScrollRight`/`canScrollLeft` — read by `*ngIf` in the
    // template — in the same tick the view was just checked, which trips
    // NG0100 (ExpressionChangedAfterItHasBeenCheckedError) whenever the tab
    // bar happens to overflow on first render.
    setTimeout(() => this.scrollActiveTabIntoView());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateScrollAffordance();
  }

  statusFor(tab: DocumentTab): SectionSaveStatus {
    return this.statuses[tab.id] ?? 'idle';
  }

  statusLabel(tab: DocumentTab): string {
    switch (this.statusFor(tab)) {
      case 'saving':
        return 'DOC_BUILDER.TABS.SAVING';
      case 'saved':
        return 'DOC_BUILDER.TABS.SAVED';
      case 'dirty':
        return 'DOC_BUILDER.UNSAVED_CHANGES';
      case 'validation-error':
      case 'api-error':
        return 'DOC_BUILDER.TABS.ERROR';
      default:
        return '';
    }
  }

  selectTab(index: number): void {
    if (this.activeIndex === index) {
      return;
    }

    this.activeIndexChange.emit(index);
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    const lastIndex = this.tabs.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectTab(index);
        return;
      default:
        return;
    }

    event.preventDefault();

    if (nextIndex !== null) {
      this.selectTab(nextIndex);
      this.focusTabAt(nextIndex);
    }
  }

  scrollByStep(direction: -1 | 1): void {
    const track = this.scrollTrackRef?.first?.nativeElement;

    if (!track) {
      return;
    }

    // `direction` is relative to the reading direction; in RTL the start of the track is on the right.
    track.scrollBy({
      left: direction * this.inlineSign(track) * Math.round(track.clientWidth * 0.7),
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
    });

    // scroll button availability updates asynchronously as the browser scrolls
    setTimeout(() => this.updateScrollAffordance(), 300);
  }

  onTrackScroll(): void {
    this.updateScrollAffordance();
  }

  trackByTab(_: number, tab: DocumentTab): string {
    return tab.id;
  }

  private focusTabAt(index: number): void {
    this.tabButtons?.get(index)?.nativeElement.focus();
  }

  private scrollActiveTabIntoView(): void {
    this.tabButtons?.get(this.activeIndex)?.nativeElement.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
    });

    this.updateScrollAffordance();
  }

  private get prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    );
  }

  private updateScrollAffordance(): void {
    const track = this.scrollTrackRef?.first?.nativeElement;

    if (!track) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }

    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    // Distance scrolled from the start edge (scrollLeft runs 0 → negative in RTL).
    const scrolled = track.scrollLeft * this.inlineSign(track);

    this.hasOverflow = maxScrollLeft > 4;
    this.canScrollLeft = scrolled > 4;
    this.canScrollRight = scrolled < maxScrollLeft - 4;
  }

  private inlineSign(track: HTMLElement): 1 | -1 {
    return getComputedStyle(track).direction === 'rtl' ? -1 : 1;
  }
}
